import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrgPermissions } from '@/hooks/useOrgPermissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  RefreshCw, 
  Search, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  Send
} from 'lucide-react';
import { SubscriptionLimitAlert } from '@/components/SubscriptionLimitAlert';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface EvolucaoContratacao {
  id: string;
  user_id: string;
  etapa_atual: string;
  etapa_mais_avancada: string;
  contrato_assinado: boolean;
  integracao_concluida: boolean;
  created_at: string;
  updated_at: string;
  email?: string;
  nome?: string;
  celular?: string;
}

export default function LogEvolucaoContratacoes() {
  const { userOrgInfo } = useOrgPermissions();
  const [evolucoes, setEvolucoes] = useState<EvolucaoContratacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [enviandoNotificacao, setEnviandoNotificacao] = useState<string | null>(null);

  const loadEvolucoes = async () => {
    if (!userOrgInfo?.orgId) return;
    
    setLoading(true);
    try {
      // Buscar evoluções
      const { data: evolucoesData, error: evolucoesError } = await supabase
        .from('evolucao_contratacao')
        .select('*')
        .eq('org_id', userOrgInfo.orgId)
        .order('updated_at', { ascending: false });

      if (evolucoesError) throw evolucoesError;

      // Para cada evolução, buscar dados da contratação
      const evolucoesComDados = await Promise.all(
        (evolucoesData || []).map(async (evolucao) => {
          const { data: contratacaoData } = await supabase
            .from('contratacao')
            .select('email, full_name, nome_completo, celular')
            .eq('id_usuario', evolucao.user_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          return {
            ...evolucao,
            email: contratacaoData?.email,
            nome: contratacaoData?.full_name || contratacaoData?.nome_completo,
            celular: contratacaoData?.celular
          };
        })
      );

      setEvolucoes(evolucoesComDados);
    } catch (error) {
      console.error('Erro ao carregar evoluções:', error);
      toast.error('Erro ao carregar log de evoluções');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvolucoes();
  }, [userOrgInfo?.orgId]);

  const handleReenviarNotificacao = async (evolucao: EvolucaoContratacao) => {
    setEnviandoNotificacao(evolucao.id);
    
    try {
      const { data, error } = await supabase.functions.invoke('webhook-progresso-contratacao', {
        body: {
          user_id: evolucao.user_id,
          etapa_mais_avancada: evolucao.etapa_mais_avancada
        }
      });

      if (error) throw error;

      toast.success('Notificação reenviada com sucesso!');
      console.log('Resposta do webhook:', data);
    } catch (error) {
      console.error('Erro ao reenviar notificação:', error);
      toast.error('Erro ao reenviar notificação');
    } finally {
      setEnviandoNotificacao(null);
    }
  };

  const getEtapaBadge = (etapa: string) => {
    const badges: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      proposta: { label: 'Proposta', variant: 'secondary' },
      dados_pessoais: { label: 'Dados Pessoais', variant: 'secondary' },
      envio_documentos: { label: 'Envio Documentos', variant: 'secondary' },
      validacao_documentos: { label: 'Validação Documentos', variant: 'secondary' },
      exame_admissional: { label: 'Exame Admissional', variant: 'secondary' },
      assinatura_contrato: { label: 'Assinatura Contrato', variant: 'secondary' },
      integracao: { label: 'Integração', variant: 'default' }
    };

    const badge = badges[etapa] || { label: etapa, variant: 'outline' };
    return <Badge variant={badge.variant}>{badge.label}</Badge>;
  };

  const filteredEvolucoes = evolucoes.filter(evolucao => {
    const searchLower = searchTerm.toLowerCase();
    return (
      evolucao.email?.toLowerCase().includes(searchLower) ||
      evolucao.nome?.toLowerCase().includes(searchLower) ||
      evolucao.celular?.includes(searchLower) ||
      evolucao.etapa_atual?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Log de Evolução de Contratações</h1>
        <Button onClick={loadEvolucoes} disabled={loading} variant="outline">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      <SubscriptionLimitAlert />

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar por nome, e-mail, telefone ou etapa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Candidato</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Etapa Atual</TableHead>
                <TableHead>Etapa Mais Avançada</TableHead>
                <TableHead>Contrato Assinado</TableHead>
                <TableHead>Última Atualização</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : filteredEvolucoes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Nenhum registro encontrado
                  </TableCell>
                </TableRow>
              ) : (
                filteredEvolucoes.map((evolucao) => (
                  <TableRow key={evolucao.id}>
                    <TableCell className="font-medium">
                      {evolucao.nome || 'N/A'}
                    </TableCell>
                    <TableCell>{evolucao.email || 'N/A'}</TableCell>
                    <TableCell>{evolucao.celular || 'N/A'}</TableCell>
                    <TableCell>{getEtapaBadge(evolucao.etapa_atual)}</TableCell>
                    <TableCell>{getEtapaBadge(evolucao.etapa_mais_avancada)}</TableCell>
                    <TableCell>
                      {evolucao.contrato_assinado ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <Clock className="h-5 w-5 text-yellow-500" />
                      )}
                    </TableCell>
                    <TableCell>
                      {format(new Date(evolucao.updated_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReenviarNotificacao(evolucao)}
                        disabled={enviandoNotificacao === evolucao.id}
                      >
                        {enviandoNotificacao === evolucao.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-2" />
                            Reenviar
                          </>
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
